import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Legal() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#1F5F5B] text-white">
        <div className="max-w-4xl mx-auto px-6 py-8 sm:py-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/25 flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="rimmoGET" className="w-12 h-12 object-cover" />
            </div>
            <div>
              <div className="font-heading font-bold text-2xl leading-none">rimmoGET</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300 mt-1.5">Mentions légales</div>
            </div>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold leading-tight">
            Conditions Générales d'Utilisation<br className="hidden sm:block" />
            <span className="text-amber-300">& Politique de confidentialité</span>
          </h1>
          <p className="text-teal-100 text-sm sm:text-base mt-3 max-w-2xl">
            Ce document décrit les règles d'utilisation de la plateforme rimmoGET et la manière dont vos données personnelles sont traitées, conformément au RGPD.
          </p>
          <div className="mt-6">
            <Link to="/signup" data-testid="legal-back-signup">
              <Button variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border border-white/25">
                <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'inscription
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        {/* Table of contents */}
        <nav className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Sommaire</div>
          <ol className="grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
            <li><a href="#cgu" className="hover:text-orange-600 hover:underline">1. Conditions Générales d'Utilisation</a></li>
            <li><a href="#rgpd" className="hover:text-orange-600 hover:underline">2. Politique de confidentialité (RGPD)</a></li>
            <li><a href="#donnees" className="hover:text-orange-600 hover:underline">3. Données collectées & finalités</a></li>
            <li><a href="#droits" className="hover:text-orange-600 hover:underline">4. Vos droits</a></li>
            <li><a href="#securite" className="hover:text-orange-600 hover:underline">5. Sécurité</a></li>
            <li><a href="#contact" className="hover:text-orange-600 hover:underline">6. Contact</a></li>
          </ol>
        </nav>

        {/* CGU */}
        <section id="cgu" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">1. Conditions Générales d'Utilisation</h2>
          </div>

          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
            <h3 className="font-heading text-lg font-semibold text-slate-900">1.1 Objet</h3>
            <p>
              rimmoGET est une plateforme SaaS de gestion locative destinée aux agences immobilières et bailleurs. Elle permet
              la gestion des propriétaires, maisons, locataires, paiements de loyers, dépenses et la génération de documents
              (contrats, reçus).
            </p>

            <h3 className="font-heading text-lg font-semibold text-slate-900">1.2 Acceptation</h3>
            <p>
              L'utilisation de rimmoGET implique l'acceptation pleine et entière des présentes conditions. En créant un compte
              vous certifiez avoir lu et compris ces conditions et vous engagez à les respecter.
            </p>

            <h3 className="font-heading text-lg font-semibold text-slate-900">1.3 Inscription et validation</h3>
            <p>
              L'inscription se fait via le formulaire public. Chaque demande est validée manuellement par la direction avant
              activation. Les informations fournies doivent être exactes et à jour. Un compte par société est autorisé.
            </p>

            <h3 className="font-heading text-lg font-semibold text-slate-900">1.4 Compte et sécurité</h3>
            <p>
              Vous êtes responsable de la confidentialité de votre mot de passe et de toute activité effectuée depuis votre
              compte. Tout usage frauduleux doit être signalé immédiatement à la direction.
            </p>

            <h3 className="font-heading text-lg font-semibold text-slate-900">1.5 Utilisation acceptable</h3>
            <p>Vous vous engagez à :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Ne pas tenter d'accéder aux données d'autres sociétés (isolation multi-tenant stricte) ;</li>
              <li>Ne pas contourner les mécanismes de sécurité (rate-limiting, quotas) ;</li>
              <li>Ne pas utiliser la plateforme à des fins illégales ou contraires aux bonnes mœurs ;</li>
              <li>Respecter les droits des personnes dont vous saisissez les données (locataires, propriétaires).</li>
            </ul>

            <h3 className="font-heading text-lg font-semibold text-slate-900">1.6 Quotas et abonnement</h3>
            <p>
              Chaque société dispose d'un quota d'utilisateurs et d'une durée d'abonnement définie par la direction. À
              l'expiration ou à la fermeture, le compte passe en mode lecture seule : consultation possible, mutations bloquées.
            </p>

            <h3 className="font-heading text-lg font-semibold text-slate-900">1.7 Propriété intellectuelle</h3>
            <p>
              La plateforme, sa charte graphique, son code et sa marque restent la propriété exclusive de rimmoGET. Les données
              que vous saisissez restent votre propriété.
            </p>

            <h3 className="font-heading text-lg font-semibold text-slate-900">1.8 Modification & résiliation</h3>
            <p>
              rimmoGET se réserve le droit de modifier les présentes CGU à tout moment. La direction peut suspendre ou clôturer
              tout compte en cas de manquement grave après notification.
            </p>
          </div>
        </section>

        {/* RGPD */}
        <section id="rgpd" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">2. Politique de confidentialité (RGPD)</h2>
          </div>

          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
            <h3 className="font-heading text-lg font-semibold text-slate-900">2.1 Responsable de traitement</h3>
            <p>
              Le responsable de traitement des données personnelles est l'éditeur de rimmoGET. Toute demande relative à vos
              données peut être adressée par email à la direction (voir section Contact).
            </p>

            <h3 id="donnees" className="font-heading text-lg font-semibold text-slate-900">2.2 Données collectées</h3>
            <p>Nous collectons uniquement les données strictement nécessaires au fonctionnement du service :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Compte administrateur</strong> : email, mot de passe (hashé bcrypt), nom de la société, téléphone.</li>
              <li><strong>Données métier</strong> : propriétaires, locataires (nom, contact), maisons, paiements, dépenses saisis par vous.</li>
              <li><strong>Données techniques</strong> : adresse IP (pour rate-limiting sécurité), timestamps de connexion.</li>
            </ul>
            <p>
              Aucune donnée bancaire, aucun cookie de traçage tiers, aucune publicité ne sont utilisés.
            </p>

            <h3 className="font-heading text-lg font-semibold text-slate-900">2.3 Finalités</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Fournir le service de gestion locative ;</li>
              <li>Garantir la sécurité de la plateforme (détection abus, rate-limiting) ;</li>
              <li>Communiquer avec vous (support, notifications de compte).</li>
            </ul>

            <h3 className="font-heading text-lg font-semibold text-slate-900">2.4 Base légale</h3>
            <p>
              Le traitement est fondé sur l'exécution du contrat (fourniture du service) et sur votre consentement explicite
              lors de la création du compte (case à cocher CGU).
            </p>

            <h3 className="font-heading text-lg font-semibold text-slate-900">2.5 Durée de conservation</h3>
            <p>
              Les données sont conservées pendant toute la durée de votre abonnement, puis pendant 12 mois supplémentaires en
              archive (pour permettre une éventuelle réactivation), avant suppression définitive.
            </p>

            <h3 className="font-heading text-lg font-semibold text-slate-900">2.6 Partage et sous-traitants</h3>
            <p>
              Vos données ne sont jamais vendues ni cédées à des tiers. Elles sont hébergées sur des serveurs sécurisés et
              n'accessibles qu'à l'équipe technique habilitée de rimmoGET.
            </p>
          </div>
        </section>

        {/* Vos droits */}
        <section id="droits" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">3. Vos droits</h2>
          </div>
          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed">
            <p>Conformément au RGPD, vous disposez à tout moment des droits suivants :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Droit d'accès</strong> : consulter les données que nous détenons sur vous ;</li>
              <li><strong>Droit de rectification</strong> : corriger toute information inexacte ;</li>
              <li><strong>Droit à l'effacement</strong> ("droit à l'oubli") : demander la suppression de votre compte et de vos données ;</li>
              <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format lisible (CSV, JSON) ;</li>
              <li><strong>Droit d'opposition</strong> : vous opposer à certains traitements ;</li>
              <li><strong>Droit à la limitation</strong> : demander la suspension du traitement de vos données.</li>
            </ul>
            <p>Pour exercer ces droits, contactez-nous à l'adresse indiquée en section Contact. Nous répondons sous 30 jours maximum.</p>
          </div>
        </section>

        {/* Sécurité */}
        <section id="securite" className="space-y-4">
          <h2 className="font-heading text-2xl font-bold text-slate-900">4. Sécurité</h2>
          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed">
            <ul className="list-disc pl-6 space-y-1">
              <li>Mots de passe hashés avec bcrypt (jamais stockés en clair) ;</li>
              <li>Communications chiffrées en HTTPS/TLS ;</li>
              <li>Isolation stricte des données entre sociétés (multi-tenant) ;</li>
              <li>Rate-limiting sur les endpoints sensibles (login, signup) ;</li>
              <li>Sauvegardes régulières de la base de données ;</li>
              <li>Journalisation des accès et actions sensibles.</li>
            </ul>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="space-y-4">
          <h2 className="font-heading text-2xl font-bold text-slate-900">5. Contact</h2>
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-slate-700 leading-relaxed">
              Pour toute question relative aux présentes conditions, à la protection de vos données ou pour exercer vos
              droits, contactez la direction de rimmoGET par le canal de communication qui vous a été fourni lors de votre
              inscription.
            </p>
          </div>
        </section>

        {/* Footer CTA */}
        <div className="pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            Dernière mise à jour : février 2026
          </p>
          <Link to="/signup">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="legal-cta-signup">
              Revenir au formulaire d'inscription
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
